import { useEffect, useRef, useState } from 'react'

type PersistedHeight = {
  height: number
  containerRef: (node: HTMLDivElement | null) => void
}

export const usePersistedHeight = (
  key: string | null,
  defaultHeight: number,
  minHeight: number = 0
): PersistedHeight => {
  const [height, setHeight] = useState<number>(() => {
    if (!key) return defaultHeight
    try {
      const saved = localStorage.getItem(key)
      const parsed = saved !== null ? parseInt(saved, 10) : NaN
      return Number.isFinite(parsed) && parsed >= minHeight ? parsed : defaultHeight
    } catch {
      return defaultHeight
    }
  })

  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const observerRef = useRef<ResizeObserver>()

  const containerRef = (node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    if (!node) return

    observerRef.current = new ResizeObserver((entries) => {
      const observedHeight = Math.round(entries[0].contentRect.height)

      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setHeight(observedHeight)
        if (!key) return
        try {
          localStorage.setItem(key, String(observedHeight))
        } catch {
          //
        }
      }, 300)
    })
    observerRef.current.observe(node)
  }

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return { height, containerRef }
}
